
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { UpdateForm } from './automated-updates/UpdateForm';
import { UpdatesTable } from './automated-updates/UpdatesTable';
import { useMutations } from './automated-updates/useMutations';
import { AutomatedUpdate } from './automated-updates/types';
import { useBulkUpdates } from './automated-updates/useBulkUpdates';
import { useUpdateForm } from './automated-updates/useUpdateForm';
import { useSelectOptions } from './automated-updates/useSelectOptions';
import { UpdateActions } from './automated-updates/UpdateActions';

export const AutomatedUpdatesManager = () => {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  
  // Custom hooks for state and logic
  const { 
    resetForm, 
    editingId, 
    formData, 
    setFormData, 
    handleEdit 
  } = useUpdateForm(setIsDialogOpen);
  
  const { 
    selectedUpdates, 
    handleCheckboxChange, 
    handleBulkFrequencyUpdate, 
    handleDeleteSelected 
  } = useBulkUpdates();
  
  const { uniqueCountries, availableLeagues, availableYears, frequencies } = useSelectOptions(formData);
  
  const { 
    addUpdateMutation, 
    updateMutation, 
    deleteMutation, 
    toggleActiveMutation,
    processUpdatesMutation 
  } = useMutations(resetForm, setIsDialogOpen);

  const { data: updates, isLoading } = useQuery({
    queryKey: ['automated-updates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('automated_updates')
        .select('*')
        .order('last_updated', { ascending: false });

      if (error) throw error;
      return data as AutomatedUpdate[];
    },
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.country || !formData.league || !formData.season || !formData.excel_url) {
      return;
    }

    try {
      const { data: existingUpdate, error: checkError } = await supabase
        .from('automated_updates')
        .select('id')
        .eq('country', formData.country)
        .eq('league', formData.league)
        .eq('season', formData.season)
        .maybeSingle();

      if (checkError) {
        throw checkError;
      }

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data: formData });
      } else if (existingUpdate) {
        // This case is handled by the toast in useMutations.ts
      } else {
        await addUpdateMutation.mutateAsync(formData);
      }
    } catch (error) {
      console.error('Submission error:', error);
    }
  };

  React.useEffect(() => {
    if (!isDialogOpen) {
      resetForm();
    }
  }, [isDialogOpen]);

  React.useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['automated-updates'] });
  }, [queryClient]);

  if (!isAdmin) return null;
  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Automated Updates Manager</h2>
        <UpdateActions 
          selectedUpdates={selectedUpdates}
          onBulkFrequencyUpdate={handleBulkFrequencyUpdate}
          onDeleteSelected={() => handleDeleteSelected(deleteMutation)}
          onProcessUpdates={() => processUpdatesMutation.mutate()}
          isProcessing={processUpdatesMutation.isPending}
          frequencies={frequencies}
          setIsDialogOpen={setIsDialogOpen}
        />
      </div>

      <UpdatesTable
        updates={updates || []}
        selectedUpdates={selectedUpdates}
        onCheckboxChange={handleCheckboxChange}
        onToggleActive={(id, isActive) => toggleActiveMutation.mutate({ id, is_active: isActive })}
        onEdit={handleEdit}
      />
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Automated Update' : 'Add New Automated Update'}
            </DialogTitle>
          </DialogHeader>
          <UpdateForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            editingId={editingId}
            isLoading={addUpdateMutation.isPending || updateMutation.isPending}
            uniqueCountries={uniqueCountries}
            availableLeagues={availableLeagues}
            availableYears={availableYears}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AutomatedUpdatesManager;
